import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import "@/App.css";
import AppLayout from "@/components/AppLayout";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import ResetPassword from "@/pages/ResetPassword";
import GoogleCallback from "@/pages/GoogleCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCourseSurveyResults from "@/pages/AdminCourseSurveyResults";
import AdminCurbsideConsult from "@/pages/AdminCurbsideConsult";
import CourseContentEditor from "@/pages/CourseContentEditor";
import StudentDashboard from "@/pages/StudentDashboard";
import CourseDirectoryPage from "@/pages/CourseDirectoryPage";
import CoursePreviewPage from "@/pages/CoursePreviewPage";
import StudentResultsDashboard from "@/pages/StudentResultsDashboard";
import StudentManagement from "@/pages/StudentManagement";
import AdminStudents from "@/pages/AdminStudents";
import AdminAGDReport from "@/pages/AdminAGDReport";
import CoursePage from "@/pages/CoursePage";
import CourseSurvey from "@/pages/CourseSurvey";
import CurbsideConsult from "@/pages/CurbsideConsult";
import AdminPracticeResources from "@/pages/AdminPracticeResources";
import AdminLandingPage from "@/pages/AdminLandingPage";
import PracticeResources from "@/pages/PracticeResources";
import AdminLogin from "@/pages/AdminLogin";
import StudentLogin from "@/pages/StudentLogin";
import TestLogin from "@/pages/TestLogin";
import TestVideoPlayer from "@/pages/TestVideoPlayer";
import TestVideoDebug from "@/pages/TestVideoDebug";
import TestVideoNoStrict from "@/pages/TestVideoNoStrict";
import TestSurveySubmit from "@/pages/TestSurveySubmit";
import LoadingQuip from "@/components/LoadingQuip";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
// Pricing Pages
import PricingSingleCourse from "@/pages/PricingSingleCourse";
import PricingCeMembership from "@/pages/PricingCeMembership";
import PricingCurbsideConsult from "@/pages/PricingCurbsideConsult";
import CheckoutPage from "@/pages/CheckoutPage";
import CECreditPage from "@/pages/CECreditPage";
import CheckoutSuccess from "@/pages/CheckoutSuccess";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for session_id in URL fragment
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=")) {
      const sessionId = hash.split("session_id=")[1].split("&")[0];
      handleAuth(sessionId);
      // Clean URL
      window.history.replaceState(null, "", window.location.pathname);
    } else {
      checkAuth();
    }
  }, []);

  const handleAuth = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API}/auth/session`,
        {},
        { headers: { "X-Session-ID": sessionId } }
      );
      
      const { session_token, user } = response.data;
      localStorage.setItem("session_token", session_token);
      setUser(user);
      setLoading(false);
    } catch (error) {
      console.error("Auth error:", error);
      setLoading(false);
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem("session_token");
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem("session_token");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    const token = localStorage.getItem("session_token");
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("session_token");
    setUser(null);
    window.location.href = "/";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <LoadingQuip size="lg" />
      </div>
    );
  }

  return (
    <ThemeProvider>
      <div className="App">
        <Toaster position="top-right" />
        <BrowserRouter>
          <AppLayout>
          <Routes>
          <Route path="/" element={
            !user ? <LandingPage /> : 
            user.role === "admin" ? <Navigate to="/admin" /> :
            <Navigate to="/dashboard" />
          } />
          
          <Route path="/auth" element={
            !user ? <AuthPage /> : 
            user.role === "admin" ? <Navigate to="/admin" /> :
            <Navigate to="/dashboard" />
          } />
          
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Pricing Detail Pages */}
          <Route path="/pricing/single-course" element={<PricingSingleCourse />} />
          <Route path="/pricing/ce-membership" element={<PricingCeMembership />} />
          <Route path="/pricing/curbside-consult" element={<PricingCurbsideConsult />} />
          
          {/* Checkout Pages */}
          <Route path="/checkout" element={<CheckoutPage user={user} onLogin={setUser} />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route path="/auth/google/success" element={<GoogleCallback />} />
          
          <Route path="/admin" element={
            user && user.role === "admin" ? 
            <AdminDashboard user={user} logout={logout} /> : 
            <Navigate to="/" />
          } />
          
          <Route path="/admin/agd-report" element={
            user && user.role === "admin" ? 
            <AdminAGDReport user={user} logout={logout} /> : 
            <Navigate to="/" />
          } />
          
          <Route path="/admin/students" element={
            user && user.role === "admin" ? 
            <AdminStudents user={user} logout={logout} /> : 
            <Navigate to="/" />
          } />
          
          <Route path="/admin/student-results" element={
            user && user.role === "admin" ? 
            <StudentResultsDashboard user={user} logout={logout} /> : 
            <Navigate to="/" />
          } />
          
          <Route path="/admin/course-survey-results" element={
            user && user.role === "admin" ? 
            <AdminCourseSurveyResults user={user} logout={logout} /> : 
            <Navigate to="/" />
          } />
          
          <Route path="/admin/curbside-consult" element={
            user && user.role === "admin" ? 
            <AdminCurbsideConsult user={user} logout={logout} /> : 
            <Navigate to="/" />
          } />
          
          <Route path="/admin/practice-resources" element={
            user && user.role === "admin" ? 
            <AdminPracticeResources user={user} logout={logout} /> : 
            <Navigate to="/" />
          } />
          
          <Route path="/admin/landing-page" element={
            user && user.role === "admin" ? 
            <AdminLandingPage user={user} logout={logout} /> : 
            <Navigate to="/" />
          } />
          
          <Route path="/admin/course/:courseId/editor" element={
            user && user.role === "admin" ? 
            <CourseContentEditor user={user} logout={logout} /> : 
            <Navigate to="/" />
          } />
          
          <Route path="/dashboard" element={
            user && (user.role === "student" || user.role === "admin") ? 
            <StudentDashboard user={user} logout={logout} setUser={setUser} /> : 
            <Navigate to="/" />
          } />
          
          <Route path="/course/:courseId" element={
            user && (user.role === "student" || user.role === "admin") ? 
            <CoursePage user={user} logout={logout} /> : 
            <Navigate to="/" />
          } />
          
          <Route path="/course-survey" element={
            user && (user.role === "student" || user.role === "admin") ? 
            <CourseSurvey user={user} logout={logout} /> : 
            <Navigate to="/" />
          } />
          
          <Route path="/curbside-consult" element={
            user && (user.role === "student" || user.role === "admin") ? 
            <CurbsideConsult user={user} logout={logout} /> : 
            <Navigate to="/" />
          } />
          
          <Route path="/practice-resources" element={
            user && (user.role === "student" || user.role === "admin") ? 
            <PracticeResources user={user} logout={logout} /> : 
            <Navigate to="/" />
          } />
          
          <Route path="/courses" element={<CourseDirectoryPage user={user} logout={logout} />} />
          <Route path="/course-preview/:courseId" element={<CoursePreviewPage user={user} setUser={setUser} logout={logout} />} />
          <Route path="/ce/:courseId" element={<CECreditPage />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/student-login" element={<StudentLogin />} />
          <Route path="/test-login" element={<TestLogin />} />
          <Route path="/test-video-player" element={<TestVideoPlayer />} />
          <Route path="/test-video-debug" element={<TestVideoDebug />} />
          <Route path="/test-video-no-strict" element={<TestVideoNoStrict />} />
          <Route path="/test-survey-submit" element={<TestSurveySubmit />} />
          </Routes>
          </AppLayout>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;